<template>
  <div role="tree" class="flex flex-col min-w-0" @keydown="handleKeyDown">
    <slot v-if="showNoData && options.length <= 0" name="no-data">
      <FlotoNoData v-if="showNoData && options.length <= 0" size="small" />
    </slot>

    <MarkedContent
      v-for="(option, index) in options"
      :key="option.id"
      class="tree-single-item"
      :text="highlightTerm"
    >
      <div>
        <div class="flex items-center item-container">
          <!-- Expand/Collapse Icon -->
          <a
            v-if="(option.children || []).length && !option.expanded"
            class="text-neutral marked-content-a -ml-3"
            role="button"
            aria-expanded="false"
            :aria-controls="option.name"
            @click="option.expanded = true"
          >
            <slot name="expand-icon">
              <MIcon name="chevron-right" />
            </slot>
          </a>
          <a
            v-if="(option.children || []).length && option.expanded"
            class="text-neutral marked-content-a -ml-3"
            role="button"
            aria-expanded="true"
            :aria-controls="option.name"
            @click="option.expanded = false"
          >
            <slot name="collapse-icon">
              <MIcon name="chevron-down" />
            </slot>
          </a>

          <!-- Item Text -->
          <div
            :id="option.name"
            class="item-text flex-grow rounded mb-1"
            :class="{
              active: isSelected(option),
              disabled: isDisabled(option),
            }"
            role="treeitem"
            aria-selected="false"
            :aria-disabled="isDisabled(option) ? 'true' : 'false'"
            tabindex="0"
          >
            <slot
              name="item"
              :item="option"
              :select="() => handleChange(option)"
            >
              <div
                :class="{ disabled: isDisabled(option) }"
                class="cursor-pointer block py-1 pl-2 pr-1 rounded mb-1"
                :style="{ margin: '2px 0px', height: 'auto' }"
                @click="handleChange(option)"
              >
                {{ option.name }}
              </div>
            </slot>
          </div>
        </div>

        <!-- Children Nodes (if any) -->
        <div
          v-if="(option.children || []).length && option.expanded"
          :style="
            isRtl ? { marginRight: '40px' } : { marginLeft: `${levelMargin}px` }
          "
        >
          <TreeList
            v-bind="$attrs"
            :multiple="multiple"
            :highlight-term="highlightTerm"
            :show-no-data="showNoData"
            :level="level + 1"
            :options="option.children"
            :level-margin="levelMargin"
            :only-leaf-node-selectable="onlyLeafNodeSelectable"
            :value="value"
            :is-child-node="false"
            @change="$listeners.change"
          >
            <template v-slot:item="{ item, select }">
              <slot
                name="item"
                :item="item"
                :select="select"
                :disabled="onlyLeafNodeSelectable"
              >
              </slot>
            </template>
          </TreeList>
        </div>
      </div>
    </MarkedContent>
  </div>
</template>

<script>
import MarkedContent from '@modules/admin/components/marked-content'
import { authComputed } from '@state/modules/auth'
import { keyEvents } from '@utils/keyevents'

export default {
  name: 'TreeList',
  components: {
    MarkedContent,
  },
  props: {
    highlightTerm: { type: String, default: undefined },
    value: { type: [Number, Array, String], default: undefined },
    multiple: { type: Boolean, default: false },
    // eslint-disable-next-line
    showNoData: { type: Boolean, default: true },
    levelMargin: { type: Number, default: 20 },
    level: { type: Number, default: 0 },
    options: {
      type: Array,
      default() {
        return []
      },
    },
    // eslint-disable-next-line
    isChildNode: { type: Boolean, default: true },
    onlyLeafNodeSelectable: { type: Boolean, default: false },
  },
  data() {
    return {
      focusedNode: null,
    }
  },
  computed: {
    ...authComputed,
    getVisibleOptions() {
      return this.flattenOptions(this.options)
    },
  },
  methods: {
    handleChange(option) {
      // check leaf node only selection
      if (this.isDisabled(option) || this.isSelected(option)) {
        return
      }
      const value = this.multiple ? this.value || [] : this.value
      this.$emit(
        'change',
        Array.isArray(value) ? [...value, option.id] : option.id
      )
    },
    flattenOptions(options, result = []) {
      for (let option of options) {
        result.push(option) // Add the current option
        if (option.children && option.expanded) {
          this.flattenOptions(option.children, result) // Recursively add children
        }
      }
      return result
    },
    isSelected(option) {
      const value = this.value
      if (!value) {
        return false
      }
      return Array.isArray(value)
        ? value.indexOf(option.id) >= 0
        : value === option.id
    },
    isDisabled(option) {
      return (option.children || []).length && this.onlyLeafNodeSelectable
    },

    // Handle Arrow Key navigation (Left, Right, Up, Down)
    handleKeyDown(event) {
      if (this.isChildNode) {
        const focusedElement = document.activeElement
        const focusedOptionId = focusedElement?.id
        const focusedOption = this.findOptionById(focusedOptionId)

        if (!focusedOption) return

        const visibleOptions = this.getVisibleOptions
        const currentIndex = visibleOptions.findIndex(
          (opt) => opt === focusedOption
        )

        if (event.key === keyEvents.ARROW_DOWN) {
          // Navigate to the next visible option
          const nextOption = visibleOptions[currentIndex + 1]
          if (nextOption) {
            this.focusOption(nextOption)
          }
        } else if (event.key === keyEvents.ARROW_UP) {
          // Navigate to the previous visible option
          const prevOption = visibleOptions[currentIndex - 1]
          if (prevOption) {
            this.focusOption(prevOption)
          }
        } else if (event.key === keyEvents.ARROW_RIGHT) {
          // Expand node if collapsed
          if (focusedOption.children && !focusedOption.expanded) {
            focusedOption.expanded = true
            this.focusOption(focusedOption) // Keep focus on expanded node
          }
        } else if (event.key === keyEvents.ARROW_LEFT) {
          // Collapse node if expanded
          if (focusedOption.expanded) {
            focusedOption.expanded = false
            this.focusOption(focusedOption) // Keep focus on collapsed node
          }
        } else if (event.key === 'Home') {
          const visibleOptions = this.getVisibleOptions
          if (visibleOptions.length > 0) {
            this.focusOption(visibleOptions[0])
          }
        } else if (event.key === 'End') {
          const visibleOptions = this.getVisibleOptions
          if (visibleOptions.length > 0) {
            this.focusOption(visibleOptions[visibleOptions.length - 1])
          }
        } else if (event.key === keyEvents.ENTER) {
          // Select the focused option
          this.handleChange(focusedOption)
        }
      }
    },
    // Find the index of an option in the tree, including nested children
    findOptionIndex(option) {
      const findRecursively = (options, targetOption) => {
        for (let i = 0; i < options.length; i++) {
          const opt = options[i]
          if (opt === targetOption) {
            return i // Found the option, return its index
          }
          if (opt.children && opt.children.length > 0) {
            // Recursively check child nodes
            const childIndex = findRecursively(opt.children, targetOption)
            if (childIndex >= 0) {
              return childIndex
            }
          }
        }
        return -1 // Option not found
      }
      return findRecursively(this.options, option)
    },

    // Focus an option (for keyboard navigation)
    focusOption(option) {
      const element = document.getElementById(option.systemName)
      if (element) {
        this.focusedNode = option
        element.focus()
      }
    },

    // Get the next navigable option (including children and siblings)
    getNextNavigableOption(option) {
      // If the current option has children and is expanded, focus the first child
      if (option.children && option.expanded) {
        return option.children[0] // First child
      }

      // Check for the next sibling
      const nextSibling = this.getNextSiblingOption(option)
      if (nextSibling) {
        return nextSibling
      }

      // If no sibling, traverse up the tree to find the next sibling of an ancestor
      let parent = this.findParentOption(option)
      while (parent) {
        const parentSibling = this.getNextSiblingOption(parent)
        if (parentSibling) {
          return parentSibling
        }
        parent = this.findParentOption(parent) // Keep going up
      }

      return null // No next option
    },
    // Get the next sibling of an option (flat level)
    getNextSiblingOption(option) {
      const parent = this.findParentOption(option)
      if (parent) {
        const index = parent.children.indexOf(option)
        return parent.children[index + 1]
      }
      return null // No next sibling
    },

    // Get the previous navigable option (including nested options)
    getPrevNavigableOption(option) {
      const parent = this.findParentOption(option)

      if (parent) {
        const index = this.findOptionIndex(option)
        const prevSibling = parent.children[index - 1]

        if (prevSibling) {
          return prevSibling
        } else {
          // If no previous sibling, go up to the parent and check its previous sibling
          return parent
        }
      }

      return null // No previous option
    },

    // Find the parent of a given option
    findParentOption(option) {
      const findRecursively = (options, targetOption) => {
        for (let opt of options) {
          if (opt.children && opt.children.includes(targetOption)) {
            return opt // Parent found
          }
          if (opt.children && opt.children.length > 0) {
            const parent = findRecursively(opt.children, targetOption)
            if (parent) {
              return parent // Parent found in children
            }
          }
        }
        return null // No parent found
      }
      return findRecursively(this.options, option)
    },

    // Find an option by its ID
    findOptionById(id) {
      const findRecursively = (options, targetId) => {
        for (let opt of options) {
          if (opt.name === targetId) {
            return opt
          }
          if (opt.children && opt.children.length > 0) {
            const found = findRecursively(opt.children, targetId)
            if (found) return found
          }
        }
        return null
      }
      return findRecursively(this.options, id)
    },
  },
}
</script>

<style lang="less" scoped>
.tree-single-item {
  border-radius: 0.375rem;

  .item-text {
    &:hover,
    &:focus-visible,
    &.active {
      color: var(--dropdown-selected-text);
      background: var(--dropdown-hover-bg);
    }

    &.disabled {
      background: unset;
      opacity: 0.5;
    }
  }
}
</style>
